import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { useToast } from './Toast';

type Candidate = { url: string; prompt: string };

/**
 * Modal that triggers a best-of-N generation as soon as it opens, then shows
 * the candidates in a grid for the admin to pick from. Picking commits the
 * URL as the workshop's coverImageUrl and closes.
 *
 * Unselected candidates remain as orphans in R2 — small cost, keeps the
 * picker UX simple. A sweep job can clean them up later if storage grows.
 */
export function CoverCandidatePicker({
  workshopId,
  open,
  onClose,
  onSelected,
  n = 3,
}: {
  workshopId: string;
  open: boolean;
  onClose: () => void;
  onSelected: () => void;
  n?: number;
}) {
  const toast = useToast();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [picking, setPicking] = useState<string | null>(null);

  const generate = useMutation({
    mutationFn: () => api.admin.generateCoverCandidates(workshopId, n),
    onSuccess: (r) => {
      setCandidates(r.candidates);
      setErrors(r.errors);
      if (r.errors.length > 0 && r.candidates.length > 0) {
        toast.info(
          'Partial result',
          `${r.candidates.length} of ${n} generated. ${r.errors.length} failed.`,
        );
      }
    },
    onError: (err) => {
      toast.error(
        'Generation failed',
        err instanceof ApiError ? err.message : String(err),
      );
    },
  });
  const generating = generate.isPending;

  const select = useMutation({
    mutationFn: (c: Candidate) =>
      api.admin.selectCoverCandidate(workshopId, c.url, c.prompt),
    onSuccess: () => {
      toast.success('Cover updated');
      onSelected();
      onClose();
    },
    onError: (err) => {
      setPicking(null);
      toast.error('Pick failed', err instanceof ApiError ? err.message : String(err));
    },
  });

  // Kick off generation when the modal opens; clear when it closes so the
  // next open starts fresh instead of showing stale candidates.
  useEffect(() => {
    if (open) {
      setCandidates([]);
      setErrors([]);
      setPicking(null);
      generate.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl rounded-2xl bg-surface p-6 shadow-cardHover"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold tracking-tight">Pick a cover</h2>
            <p className="mt-1 text-sm text-ink-muted">
              {generating
                ? `Generating ${n} options in parallel — usually ~30 seconds.`
                : candidates.length > 0
                  ? 'Click one to set it as this workshop\'s cover. Others will be discarded.'
                  : 'No candidates generated.'}
            </p>
          </div>
          <button
            type="button"
            className="text-sm text-ink-muted hover:text-ink"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        {generating ? <SkeletonGrid n={n} /> : null}

        {!generating && candidates.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {candidates.map((c, i) => (
              <CandidateCard
                key={c.url}
                candidate={c}
                index={i}
                picking={picking === c.url}
                disabled={select.isPending}
                onPick={() => {
                  setPicking(c.url);
                  select.mutate(c);
                }}
              />
            ))}
          </div>
        ) : null}

        {!generating && errors.length > 0 ? (
          <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50/60 p-3 text-xs text-amber-900">
            <strong>{errors.length} of {n} failed to generate.</strong>
            <ul className="mt-1 list-inside list-disc">
              {errors.slice(0, 3).map((e, i) => (
                <li key={i} className="truncate">{e}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {!generating && candidates.length === 0 && errors.length === 0 ? (
          <p className="text-sm text-ink-muted">Nothing to show.</p>
        ) : null}

        {!generating ? (
          <div className="mt-5 flex justify-between">
            <button
              type="button"
              className="btn-secondary"
              disabled={select.isPending}
              onClick={() => generate.mutate()}
            >
              Generate again
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={select.isPending}
            >
              Cancel
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function CandidateCard({
  candidate,
  index,
  picking,
  disabled,
  onPick,
}: {
  candidate: Candidate;
  index: number;
  picking: boolean;
  disabled: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      className={`group relative overflow-hidden rounded-xl border-2 text-left transition ${
        picking
          ? 'border-accent ring-2 ring-accent/30'
          : 'border-line hover:border-accent hover:shadow-card'
      } ${disabled && !picking ? 'opacity-40' : ''}`}
    >
      <img
        src={candidate.url}
        alt={`Candidate ${index + 1}`}
        className="aspect-video w-full bg-canvas object-cover"
      />
      <div className="flex items-center justify-between px-3 py-2 text-xs">
        <span className="text-ink-muted">Option {index + 1}</span>
        <span className={picking ? 'font-semibold text-accent' : 'font-medium text-ink'}>
          {picking ? 'Saving…' : 'Pick this →'}
        </span>
      </div>
    </button>
  );
}

function SkeletonGrid({ n }: { n: number }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-xl border border-line">
          <div className="aspect-video w-full animate-pulse bg-canvas" />
          <div className="flex items-center justify-between px-3 py-2 text-xs text-ink-subtle">
            <span>Option {i + 1}</span>
            <span>Generating…</span>
          </div>
        </div>
      ))}
    </div>
  );
}
